import { FieldLabel, TextField } from '@components/textfield';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@components/ui/dialog';
import { Input } from '@components/ui/input';
import React from 'react';
import { CommunityBudgetCurrencySupportDto } from '@api/ChallengeClient';

interface Props {
    open: boolean;
    budget?: CommunityBudgetCurrencySupportDto;
    ownedCurrencyCodes: string[];
    onClose(): void;
    onSave(
        currencyCode: string,
        budgetValue: number | undefined,
        budgetHardCap: number | undefined,
        comment: string
    ): Promise<void>;
}

export const BudgetDialog = ({
    open,
    budget,
    ownedCurrencyCodes,
    onClose,
    onSave
}: Props) => {
    const currencyListId = React.useId();
    const [currencyCode, setCurrencyCode] = React.useState('');
    const [budgetValue, setBudgetValue] = React.useState('');
    const [budgetHardCap, setBudgetHardCap] = React.useState('');
    const [comment, setComment] = React.useState('');
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState<string | undefined>(undefined);

    React.useEffect(() => {
        if (open) {
            setCurrencyCode(budget?.currencyCode ?? '');
            setBudgetValue(budget?.budgetValue?.toString() ?? '');
            setBudgetHardCap(budget?.budgetHardCap?.toString() ?? '');
            setComment('');
            setError(undefined);
        }
    }, [open, budget]);

    const parsed = (value: string) =>
        value.trim().length === 0 ? undefined : Number(value);
    const parsedValue = parsed(budgetValue);
    const parsedHardCap = parsed(budgetHardCap);

    const invalidNumber = (value: number | undefined) =>
        value !== undefined && (isNaN(value) || value < 0);
    const exceedsHardCap =
        parsedValue !== undefined &&
        parsedHardCap !== undefined &&
        parsedValue > parsedHardCap;

    const canSave =
        currencyCode.trim().length > 0 &&
        comment.trim().length > 0 &&
        !invalidNumber(parsedValue) &&
        !invalidNumber(parsedHardCap) &&
        !exceedsHardCap;

    const save = async () => {
        setSaving(true);
        setError(undefined);
        try {
            await onSave(
                currencyCode.trim(),
                parsedValue,
                parsedHardCap,
                comment.trim()
            );
            onClose();
        } catch (e) {
            setError('Could not save the budget. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>{budget ? 'Edit' : 'Add'} budget</DialogTitle>
                </DialogHeader>
                <div className="flex flex-col gap-4">
                    {!budget && (
                        <Alert variant="info">
                            <AlertDescription>
                                Adding a budget enrols this community for budget
                                tracking in that currency. Spend is only
                                recorded when the currency matches exactly.
                            </AlertDescription>
                        </Alert>
                    )}
                    <div className="flex flex-col gap-1.5">
                        <FieldLabel label="Currency" required />
                        <Input
                            list={currencyListId}
                            required
                            disabled={Boolean(budget)}
                            value={currencyCode}
                            onChange={(e) => setCurrencyCode(e.target.value)}
                        />
                        <datalist id={currencyListId}>
                            {ownedCurrencyCodes.map((code) => (
                                <option key={code} value={code} />
                            ))}
                        </datalist>
                        <p className="text-xs text-muted-foreground">
                            {budget
                                ? 'The currency identifies the budget and cannot be changed'
                                : "Pick one of the community's currencies, or type any code"}
                        </p>
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <TextField
                            label="Budget"
                            type="number"
                            value={budgetValue}
                            onChange={(value) => setBudgetValue(String(value))}
                        />
                        <p className="text-xs text-muted-foreground">
                            Leave empty for no budget
                        </p>
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <TextField
                            label="Hard cap"
                            type="number"
                            value={budgetHardCap}
                            onChange={(value) =>
                                setBudgetHardCap(String(value))
                            }
                            error={
                                exceedsHardCap
                                    ? 'The hard cap cannot be lower than the budget'
                                    : undefined
                            }
                        />
                        {!exceedsHardCap && (
                            <p className="text-xs text-muted-foreground">
                                Leave empty for no hard cap
                            </p>
                        )}
                    </div>
                    <TextField
                        label="Reason"
                        type="text"
                        required
                        multiline
                        maxRows={2}
                        value={comment}
                        onChange={(value) => setComment(String(value))}
                    />
                    {error && (
                        <Alert variant="destructive">
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button disabled={!canSave} loading={saving} onClick={save}>
                        Save
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
